import React, { useCallback, useEffect, useState } from 'react';
import { PiggyBank, ArrowDownCircle, ArrowUpCircle, Trash2, Plus, X } from 'lucide-react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { showNotification } from '../components/GlobalNotification';

/**
 * What the cafe has put by, across as many funds as it keeps, and the full
 * history of money going in and out of each.
 *
 * A deposit is not spending - the money is still the cafe's, only in a
 * different pot - so nothing here touches revenue or net profit. What a cash
 * deposit does do is leave the drawer, which the till accounts for so a session
 * still reconciles on the day the owner sweeps the takings.
 */
function Savings() {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ balance: 0, deposited: 0, withdrawn: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // null means every fund at once, which is the view an owner wants first
  const [selected, setSelected] = useState(null);

  const [type, setType] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [target, setTarget] = useState('');
  // Defaults to today, but stays editable so a Sunday's saving entered on
  // Monday still lands on the day it actually happened
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [newFund, setNewFund] = useState('');
  const [addingFund, setAddingFund] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/savings', {
        params: selected ? { accountId: selected } : undefined
      });
      setAccounts(response.data.accounts || []);
      setEntries(response.data.entries || []);
      setTotals({
        balance: response.data.balance || 0,
        deposited: response.data.deposited || 0,
        withdrawn: response.data.withdrawn || 0
      });
    } catch (error) {
      showNotification('error', t('Could not load savings.'));
    } finally {
      setLoading(false);
    }
  }, [selected, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the form pointed at something real: the fund on screen when one is
  // picked, otherwise the first that exists
  useEffect(() => {
    if (selected) {
      setTarget(selected);
      return;
    }
    setTarget((current) =>
      accounts.some((account) => account._id === current) ? current : accounts[0]?._id || ''
    );
  }, [selected, accounts]);

  const money = (value) => `Rs ${(value ?? 0).toFixed(2)}`;

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString([], {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
      : '';

  const selectedAccount = accounts.find((account) => account._id === selected) || null;
  const targetAccount = accounts.find((account) => account._id === target) || null;
  const shownBalance = selectedAccount ? selectedAccount.balance : totals.balance;

  const handleAddFund = async (event) => {
    event.preventDefault();
    const name = newFund.trim();
    if (!name) return;

    try {
      setAddingFund(true);
      const response = await api.post('/api/savings/accounts', { name });
      showNotification('success', response.data.message);
      setNewFund('');
      await load();
      setTarget(response.data.account._id);
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not create the fund.');
    } finally {
      setAddingFund(false);
    }
  };

  const handleCloseFund = async (account) => {
    if (!window.confirm(t("Close '{name}'? Its history is kept.", { name: account.name }))) return;

    try {
      const response = await api.delete(`/api/savings/accounts/${account._id}`);
      showNotification('success', response.data.message);
      if (selected === account._id) setSelected(null);
      await load();
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not close the fund.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!target) {
      showNotification('error', t('Create a fund first.'));
      return;
    }

    const value = parseFloat(amount);
    if (!Number.isFinite(value) || value <= 0) {
      showNotification('error', t('Enter an amount greater than zero.'));
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/api/savings', {
        accountId: target,
        type,
        amount: value,
        method,
        note: note.trim() || undefined,
        date
      });

      showNotification('success', response.data.message);
      setAmount('');
      setNote('');
      await load();
      // A cash movement changes what the drawer should hold
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (
      !window.confirm(
        t('Remove the {amount} {type} of {date}? The balance goes back to what it was.', {
          amount: money(entry.amount),
          type: entry.type,
          date: formatDate(entry.date)
        })
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/savings/${entry._id}`);
      showNotification('success', t('Entry removed.'));
      await load();
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not remove that entry.');
    }
  };

  const isDeposit = type === 'deposit';

  return (
    <div className="space-y-3 sm:space-y-6 max-w-3xl">
      <div className="hidden sm:block">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('Savings')}</h1>
        <p className="text-gray-600 mt-1">
          {t('What the cafe has put by, fund by fund, and every deposit and withdrawal behind it.')}
        </p>
      </div>

      {/* Balance - the fund on screen, or everything when none is picked */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6">
        <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {selectedAccount ? selectedAccount.name : t('All funds')}
        </p>
        <p className="text-3xl sm:text-4xl font-bold text-emerald-600 leading-tight">
          {money(shownBalance)}
        </p>
        <div className="flex gap-4 mt-2 text-xs text-gray-500">
          <span>
            Put in{' '}
            {money(selectedAccount ? selectedAccount.deposited : totals.deposited)}
          </span>
          <span>
            Taken out{' '}
            {money(selectedAccount ? selectedAccount.withdrawn : totals.withdrawn)}
          </span>
        </div>
      </div>

      {/* Funds */}
      <div className="space-y-2">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <button
            onClick={() => setSelected(null)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              selected === null
                ? 'bg-cafe-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t('All funds')}
          </button>
          {accounts.map((account) => (
            <button
              key={account._id}
              onClick={() => setSelected(account._id)}
              className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                selected === account._id
                  ? 'bg-cafe-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {account.name}
              <span
                className={`ml-2 font-normal ${
                  selected === account._id ? 'text-white/80' : 'text-gray-400'
                }`}
              >
                {money(account.balance)}
              </span>
            </button>
          ))}
        </div>

        <form onSubmit={handleAddFund} className="flex gap-2">
          <input
            type="text"
            value={newFund}
            onChange={(e) => setNewFund(e.target.value)}
            maxLength={60}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cafe-500"
            placeholder={t('New fund, e.g. Emergency fund')}
          />
          <button
            type="submit"
            disabled={addingFund || !newFund.trim()}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('Add')}
          </button>
        </form>

        {selectedAccount && (
          <button
            onClick={() => handleCloseFund(selectedAccount)}
            className="text-xs text-red-600 hover:text-red-700 font-medium inline-flex items-center gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Close '{selectedAccount.name}'
          </button>
        )}
      </div>

      {accounts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 text-center">
          <PiggyBank className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">{t('No funds yet')}</h3>
          <p className="text-sm text-gray-500">
            {t('Create a fund above — money is saved towards something, so a name helps.')}
          </p>
        </div>
      ) : (
        <>
          {/* Add an entry */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 space-y-4"
          >
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'deposit', label: 'Put money in', icon: ArrowDownCircle },
                { key: 'withdrawal', label: 'Take money out', icon: ArrowUpCircle }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setType(key)}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] rounded-xl border-2 text-sm font-semibold transition-colors ${
                    type === key
                      ? key === 'deposit'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Fund')}</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cafe-500"
              >
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.name} — {money(account.balance)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('Amount (Rs.)')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                  placeholder="0.00"
                  required
                />
                {/* Only meaningful when taking out - a fund cannot lend from
                    another fund's money */}
                {!isDeposit && targetAccount && (
                  <p className="text-xs text-gray-500 mt-1">
                    {money(targetAccount.balance)} available in {targetAccount.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('Date')}</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Moved by')}</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'cash', label: 'Cash from till' },
                  { key: 'bank', label: 'Bank transfer' }
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMethod(key)}
                    className={`px-3 py-2.5 min-h-[44px] rounded-xl border-2 text-sm font-medium transition-colors ${
                      method === key
                        ? 'border-cafe-500 bg-cafe-50 text-cafe-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* Only cash touches the drawer, so it is worth being explicit
                  about which of the two the owner is doing */}
              <p className="text-xs text-gray-500 mt-1.5">
                {method === 'cash'
                  ? isDeposit
                    ? 'Comes out of the till, so the drawer expects that much less.'
                    : 'Goes back into the till, so the drawer expects that much more.'
                  : 'Never passes through the drawer, so the till is unaffected.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Note (optional)')}</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-500"
                placeholder={t('What this was for')}
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className={`w-full px-4 py-3 min-h-[44px] rounded-xl text-white font-semibold transition-colors disabled:opacity-50 ${
                isDeposit ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {saving ? 'Saving...' : isDeposit ? 'Add to savings' : 'Take from savings'}
            </button>
          </form>

          {/* History */}
          <h2 className="text-base font-semibold text-gray-900">
            History{selectedAccount ? ` · ${selectedAccount.name}` : ''}
          </h2>

          {loading ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center text-sm text-gray-500">
              {t('Loading...')}
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 text-center">
              <PiggyBank className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">{t('Nothing here yet')}</h3>
              <p className="text-sm text-gray-500">
                {t('Money you put by will be listed here with the date it went in.')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry) => {
                const deposit = entry.type === 'deposit';

                return (
                  <div
                    key={entry._id}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5 flex items-center gap-3"
                  >
                    <div
                      className={`p-2 rounded-lg flex-shrink-0 ${
                        deposit ? 'bg-emerald-50' : 'bg-amber-50'
                      }`}
                    >
                      {deposit ? (
                        <ArrowDownCircle className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <ArrowUpCircle className="h-5 w-5 text-amber-600" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {entry.account?.name || 'Fund'}
                        <span className="text-gray-500 font-normal">
                          {' '}
                          · {entry.method === 'cash' ? 'cash' : 'bank'}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(entry.date)}
                        {entry.recordedByName ? ` · ${entry.recordedByName}` : ''}
                      </p>
                      {entry.note && (
                        <p className="text-xs text-gray-600 mt-0.5 truncate">{entry.note}</p>
                      )}
                    </div>

                    <span
                      className={`font-bold whitespace-nowrap ${
                        deposit ? 'text-emerald-600' : 'text-amber-600'
                      }`}
                    >
                      {deposit ? '+' : '−'} {money(entry.amount)}
                    </span>

                    <button
                      onClick={() => handleDelete(entry)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                      title={t('Remove this entry')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Savings;
